import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

/**
 * Determines if an action job is a Cury auto_heating action.
 *
 * These actions are created by Cury profiles and have:
 * - method: "Cury.SetConfig"
 * - params.config.auto_heating_on: true
 *
 * @param {ActionJob} actionJob - The action job to check
 * @returns {boolean} True if the action is a Cury auto_heating action, false otherwise
 *
 * @example
 * const action = {
 *   method: 'Cury.SetConfig',
 *   name: 'bedroom',
 *   params: { config: { auto_heating_on: true }, id: 0 }
 * };
 * const isAutoHeating = isCuryAutoHeatingAction(action); // true
 */
const isCuryAutoHeatingAction = (actionJob: ActionJob): boolean => {
	return actionJob.method === 'Cury.SetConfig' && actionJob.params?.config?.auto_heating_on === true;
};

/**
 * Filters out Cury auto_heating actions from an array of action jobs.
 *
 * This function is used in the schedule actions list editor to prevent displaying and editing
 * auto_heating actions that are managed by Cury profiles.
 *
 * @param {ActionJob[]} actions - Array of action jobs to filter
 * @returns {ActionJob[]} Filtered array containing only non-auto_heating actions
 *
 * @example
 * const filteredActions = filterCuryAutoHeatingActions(schedule.calls);
 */
export const filterCuryAutoHeatingActions = (actions: ActionJob[]): ActionJob[] => {
	return actions.filter((action) => !isCuryAutoHeatingAction(action));
};
